import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { Box, Stack, Text } from "@mantine/core";
import React, { type JSX, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import type { EligibleProduct, IntegratedSalesSteps } from "../../../types";

import { useSalesContext } from "../../../sales-context/SalesContext";
import { useAnalytics } from "../../../shared-utils/analytics/withPolymorphicClickAnalytics";
import { useIntegratedFlowStore, useProductOfferStore, useVerifiedCustomerStore } from "../../../stores";
import { CollectCustomerInfo } from "./CollectCustomerInfo/CollectCustomerInfo";
import { FullChecklistErrorFallback } from "./FullOfferChecklist/components/OfferChecklistFallbacks";
import { FullOfferChecklist } from "./FullOfferChecklist/FullOfferChecklist";
import { InlineSaleTrackerStep } from "./InlineSaleTracker/InlineSaleTrackerStep";
import { useLanguageStore } from "./language-selector/useLanguageStore";
import { SendLink } from "./SendLink/SendLink";
import { FullOfferChecklistStep } from "./StepSpecific/FullOfferChecklistStep";
import { SalesFooter } from "./StepSpecific/SalesFooter";
import { WaitForEnrollment } from "./StepSpecific/WaitForEnrollment";

export function IntegratedFlow({ product }: { product: EligibleProduct }): JSX.Element | null {
    const { sessionId, logger, taskType } = useSalesContext();
    const { dispatcher } = useAnalytics();

    const { customer } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId));
    const [contactMDN, setContactMDN] = useState(customer?.mdn ?? "");
    const [contactEmail, setContactEmail] = useState(customer?.email ?? "");

    const languageState = useLanguageStore();

    const step: IntegratedSalesSteps = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));

    const primaryProductInfo = useProductOfferStore
        .getState()
        .getProductInfo(sessionId, product.productSku, languageState.language);

    useEffect(() => {
        void dispatcher.dispatchBusinessEvent(`${step}_IntegratedFlow`);
    }, [step, dispatcher]);

    if (!taskType.isVoice && !taskType.isChat) {
        return null;
    }

    if (!primaryProductInfo) {
        logger.warn({ product, primaryProductInfo }, "Unable to find primary product info");
        return <Text>An error has occurred while trying to find plan details</Text>;
    }

    const renderStepContent = (salesStep: IntegratedSalesSteps) => {
        switch (salesStep) {
            case "SuggestedOfferStep":
                return (
                    <ErrorBoundary
                        onError={(error) => {
                            logger.error({ error }, "Error rendering full offer checklist");
                            void sendErrorMetric("FullOfferChecklistError", { eventSource: "Offer-Checklist" });
                        }}
                        fallbackRender={() => <FullChecklistErrorFallback />}
                    >
                        <FullOfferChecklistStep
                            product={product}
                            salesChecklist={primaryProductInfo?.salesOfferChecklist}
                            quickRebuttal={primaryProductInfo?.quickRebuttal}
                        />
                    </ErrorBoundary>
                );
            case "CollectCustomerInfoStep":
                return <CollectCustomerInfo />;
            case "WaitingForEnrollmentStep":
                return <WaitForEnrollment product={product} />;
            case "SendLinkStep":
                return (
                    <SendLink
                        product={product}
                        mdn={contactMDN}
                        email={contactEmail}
                        updateMDN={setContactMDN}
                        updateEmail={setContactEmail}
                        sendLinkCommTypeOverride={primaryProductInfo?.sendLinkCommType}
                        helpButtonEnabled={!!primaryProductInfo?.additionalHelpSteps}
                    />
                );
            case "HelpCustomerFindSubscribeButtonStep":
                return (
                    <Stack>
                        <Box flex={1}>
                            {primaryProductInfo?.additionalHelpSteps ? (
                                <FullOfferChecklist salesChecklist={primaryProductInfo.additionalHelpSteps} />
                            ) : (
                                <Text>Unable to find locate steps. Please file a support ticket</Text>
                            )}
                        </Box>
                        <SalesFooter
                            onBack={() =>
                                useIntegratedFlowStore.getState().updateIntegratedStep(sessionId, "SendLinkStep")
                            }
                            continueButtonProps={{ nextStep: "InlineSaleTrackerStep" }}
                            product={product}
                        />
                    </Stack>
                );
            case "InlineSaleTrackerStep":
                return (
                    <InlineSaleTrackerStep
                        productInfo={primaryProductInfo}
                        product={product}
                        productInfoLink={primaryProductInfo?.productInfoLink}
                    />
                );
            default:
                return <></>;
        }
    };

    // TODO: Fix and make sure this works for all static products
    // If no salesOfferChecklist and we're on the SuggestedOfferStep, skip to CollectCustomerInfoStep
    // const effectiveStep =
    //     !primaryProductInfo.salesOfferChecklist && step === "SuggestedOfferStep" ? "CollectCustomerInfoStep" : step;

    return renderStepContent(primaryProductInfo.salesOfferChecklist ? step : "InlineSaleTrackerStep");
}
